import { useMemo } from 'react';
import { FlexContainer, Buttons, Text, Banner } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { showChat } from 'Services/chat';
import { useTranslation } from 'Hooks/useTranslation';
import { useConfirmDeclaration } from 'Containers/Dashboard/hooks';
import { useGetUserCustomerData } from 'Containers/Datagathering/views/DgTypeForm/hooks';
import { useFetchGetUserClabe } from 'Containers/Account/components/AccountProfile/hooks';
import ResultDetailCompensation from 'Containers/Dashboard/components/TaxScreen/components/ResultDetailCompensation';
import { lottiesPaths } from 'Constants/lottiesPaths';
import { FRONT_PLANS, plansNameMapping } from 'Constants/plans';
import ResultInfo from '../ResultInfo';
import * as Styled from '../styles';
import { getResolutionDate } from '../utils';
import { ValidationViewProps } from './type';

const View = ({ tax }: ValidationViewProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.DASHBOARD);
  const { mutate } = useConfirmDeclaration();
  const { data } = useFetchGetUserClabe();
  const customerInfo = useGetUserCustomerData();
  const plan = useMemo(
    () =>
      customerInfo?.activePlan?.name
        ? plansNameMapping(customerInfo.activePlan.name)
        : FRONT_PLANS.BASIC,
    [customerInfo]
  );

  const DAYS_OFFSET = 3;

  const isFlex = plan === FRONT_PLANS.FLEX;

  const { taxYear, filingInfo, taxReturnResult } = tax;
  const isCompensation = data?.payWithExternalService;

  return (
    <FlexContainer fullWidth fullHeight direction="column" gap="dimension_16">
      {taxReturnResult?.mandatory && (
        <Banner id="warning-banner" mode="warning" label={t('Validation.chip')} layout="embed" />
      )}
      <Styled.InfoContainer justify="center" direction="column" align="center" gap="dimension_08">
        <Text size="L_L_S" color="dark" type="headline">
          {taxYear}
        </Text>
        <Text type="paragraphs" size="L_L_M" color="dark" align="center">
          {t('Dashboard.Validation.message')}
        </Text>
        <Styled.StyledLottie
          options={{ path: lottiesPaths.MORE_INFO, rendererSetting: { progressiveLoad: true } }}
        />
        {isCompensation ? (
          <ResultDetailCompensation filingInfo={filingInfo} taxReturnResult={taxReturnResult} />
        ) : (
          (filingInfo || taxReturnResult) && (
            <ResultInfo filingInfo={filingInfo} taxReturnResult={taxReturnResult} />
          )
        )}
        <Text type="paragraphs" color="dark" size="M_L_S" align="center">
          {t('Dashboard.Validation.description', {
            context: isFlex && 'isFlex',
          })}
        </Text>
        <Text type="paragraphs" color="dark" size="S_S_S" align="center">
          {t('Dashboard.Validation.info', {
            context: isFlex && 'isFlex',
            date: getResolutionDate(DAYS_OFFSET, filingInfo?.recent?.created),
          })}
        </Text>
        <Styled.ButtonsWrapper direction="column" fullWidth gap="dimension_16" align="center">
          <Styled.MultipleButtonContainer
            direction="row-reverse"
            gap="dimension_16"
            justify="center"
            fullWidth
          >
            <Buttons.Classic
              size="m"
              mode="primary"
              label={t('Dashboard.Validation.button.confirm')}
              onClick={() => mutate({ confirm: true })}
            />
            <Buttons.Classic
              size="m"
              mode="light"
              label={t('Dashboard.Validation.button')}
              onClick={showChat}
            />
          </Styled.MultipleButtonContainer>
        </Styled.ButtonsWrapper>
      </Styled.InfoContainer>
    </FlexContainer>
  );
};

export default View;
